import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CheckCircle2, Link2, Receipt, Eye, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import InvoiceCreateDialog from "@/components/admin/InvoiceCreateDialog";
import api from "@/lib/api";

export default function InvoicesTab() {
  const [items, setItems] = useState([]);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const load = async () => { const { data } = await api.get("/admin/invoices"); setItems(data); };
  useEffect(() => { load(); }, []);
  const markPaid = async (id) => { await api.post(`/admin/invoices/${id}/status`, { status: "paid" }); toast.success("Marked paid"); load(); };
  const xero = async (id) => { const { data } = await api.post(`/admin/invoices/${id}/xero-sync`); toast.info(data.message); load(); };
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">Auto-generated when a job is completed, or create one directly.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setNewInvoiceOpen(true)} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="new-invoice-btn">
            <Plus className="h-4 w-4 mr-2" />New Invoice
          </Button>
          <Button variant="outline" onClick={() => toast.info("Xero OAuth needs your Client ID & Secret in backend .env — placeholder for now.")} data-testid="connect-xero-btn"><Link2 className="h-4 w-4 mr-2" />Connect Xero</Button>
        </div>
      </div>
      {items.length === 0 ? (
        <Card className="p-16 text-center text-muted-foreground"><Receipt className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />No invoices yet.</Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-xs uppercase tracking-wider text-muted-foreground"><tr>
              <th className="p-3">Invoice #</th><th className="p-3">Customer</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Xero</th><th className="p-3 text-right"></th>
            </tr></thead>
            <tbody>
              {items.map(inv => (
                <tr key={inv.invoice_id} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="p-3">{inv.customer_name}</td>
                  <td className="p-3 font-semibold">${(inv.amount || 0).toLocaleString()}</td>
                  <td className="p-3"><Badge className={inv.status === "paid" ? "bg-emerald-500/15 text-emerald-600 border-0" : "bg-amber-500/15 text-amber-600 border-0"}>{inv.status}</Badge></td>
                  <td className="p-3">{inv.xero_synced ? <Badge className="bg-blue-500/15 text-blue-600 border-0">synced</Badge> : <Badge variant="outline">not synced</Badge>}</td>
                  <td className="p-3 text-right">
                    <Link to={`/invoice/${inv.invoice_id}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost" data-testid={`invoice-view-${inv.invoice_id}`}><Eye className="h-4 w-4 mr-1" />View</Button>
                    </Link>
                    {inv.status !== "paid" && <Button size="sm" variant="ghost" onClick={() => markPaid(inv.invoice_id)} data-testid={`invoice-mark-paid-${inv.invoice_id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Mark paid</Button>}
                    {!inv.xero_synced && <Button size="sm" variant="ghost" onClick={() => xero(inv.invoice_id)}>Sync</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <InvoiceCreateDialog directMode job={null} open={newInvoiceOpen} onClose={() => setNewInvoiceOpen(false)} onCreated={() => load()} />
    </div>
  );
}